import React from 'react'
import { Box, Avatar, Typography } from '@mui/material'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import MicOffIcon from '@mui/icons-material/MicOff'
import MicIcon from '@mui/icons-material/Mic'
import PersonIcon from '@mui/icons-material/Person'

// Common media URL prefix for profile pictures
const mediaUrlPrefix = 'https://media.aumhum.com/images/prod/'

/**
 * Parse RTM username JSON (with fallback for plain strings)
 */
function parseRtmUsername(username) {
  if (!username || typeof username !== 'string' || username.trim() === '') {
    return { 
      role: null, 
      name: 'Unknown', 
      profilePicId: null 
    }
  }
  
  try {
    const parsed = JSON.parse(username)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { 
        role: null, 
        name: username, 
        profilePicId: null 
      }
    }
    return {
      role: parsed.role || null,
      name: parsed.name || 'Unknown',
      profilePicId: parsed.profilePicId || null,
    }
  } catch (error) {
    return { 
      role: null, 
      name: username, 
      profilePicId: null 
    }
  }
}

/**
 * Custom Video Placeholder component for chamber screen when camera is turned off
 * Shows a beautiful avatar with profile picture or gradient background
 */
const ChamberVideoPlaceholder = ({ user, isShown, showButtons, showSwap, uidToUsernameMap, chamberName }) => {
  // Check if user has audio (mic is on)
  const hasAudio = user?.hasAudio === 1 || user?.hasAudio === true
  
  // State to store name from DOM (name pill) - this ensures we stay in sync with the name pill
  const [nameFromDom, setNameFromDom] = React.useState(null)
  // State to force re-render when RTM data arrives
  const [rtmDataCheckCounter, setRtmDataCheckCounter] = React.useState(0)
  
  // Watch for name pill updates in the DOM - this ensures initials stay in sync with name pill
  React.useEffect(() => {
    if (!user?.uid && user?.uid !== 0) return
    
    const findNamePill = () => {
      try {
        // Find the video card for this user - try multiple ways
        const uid = user.uid.toString()
        const uidNum = parseInt(uid)
        
        // Try multiple selectors to find the video card (including hidden elements)
        // Use querySelectorAll to find all matches, including hidden ones
        let videoCard = null
        
        // Strategy 1: Direct query
        videoCard = document.querySelector(`[data-uid="${uid}"]`) ||
                   document.querySelector(`[data-uid="${uidNum}"]`)
        
        // Strategy 2: Search all data-uid elements (including hidden ones)
        if (!videoCard) {
          const allCards = document.querySelectorAll('[data-uid]')
          for (const card of allCards) {
            const cardUid = card.getAttribute('data-uid')
            if (cardUid === uid || cardUid === uidNum.toString() || parseInt(cardUid) === uidNum) {
              videoCard = card
              break
            }
          }
        }
        
        // Strategy 3: Look for video elements and check their containers
        if (!videoCard) {
          const allVideos = document.querySelectorAll('video')
          for (const video of allVideos) {
            // Check parent containers for data-uid
            let container = video.closest('[data-uid]')
            if (!container) {
              container = video.parentElement?.closest('[data-uid]')
            }
            if (container) {
              const containerUid = container.getAttribute('data-uid')
              if (containerUid && (parseInt(containerUid) === uidNum || containerUid === uid)) {
                videoCard = container
                break
              }
            }
          }
        }
        
        // Strategy 4: Search entire document for any element with matching UID
        if (!videoCard) {
          const allElements = document.querySelectorAll('*')
          for (const el of allElements) {
            const dataUid = el.getAttribute('data-uid')
            if (dataUid && (parseInt(dataUid) === uidNum || dataUid === uid)) {
              videoCard = el
              break
            }
          }
        }
        
        if (!videoCard) {
          return null
        }
        
        // Look for name pill - try multiple selectors that match the name pill styling
        const namePillSelectors = [
          'p[style*="background"]',
          'span[style*="background"]',
          'div[style*="background"][style*="position"]',
          '[class*="username"]',
          'p',
          'span',
          'div[style*="position"]',
        ]
        
        for (const selector of namePillSelectors) {
          const elements = videoCard.querySelectorAll(selector)
          for (const el of elements) {
            const text = (el.textContent || el.innerText || '').trim()
            // Skip if it's JSON, "User {uid}", or other non-name text
            if (text && 
                text.length > 0 && 
                text.length < 100 &&
                !text.includes('{') && 
                !text.includes('"name"') && 
                !text.includes('"role"') &&
                text !== `User ${uid}` &&
                text !== `User ${uidNum}` &&
                text !== 'Loading...' &&
                text !== 'Unknown' &&
                text !== 'Guest' &&
                !text.match(/^\d+$/) &&
                !text.match(/^User \d+$/)) {
              return text
            }
          }
        }
      } catch (e) {
        console.warn('Error finding name pill:', e)
      }
      return null
    }
    
    // Try to find name pill immediately
    const foundName = findNamePill()
    if (foundName && foundName !== nameFromDom) {
      setNameFromDom(foundName)
    }
    
    // Set up MutationObserver to watch for name pill updates
    const observer = new MutationObserver(() => {
      const foundName = findNamePill()
      if (foundName && foundName !== nameFromDom) {
        setNameFromDom(foundName)
      }
    })
    
    // Observe the container for changes - use a broader scope
    const container = document.querySelector('[data-uid]')?.closest('div') || 
                     document.querySelector('[class*="video"]')?.closest('div') ||
                     document.body
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    })
    
    // Also check periodically as a fallback (more frequently)
    const interval = setInterval(() => {
      const foundName = findNamePill()
      if (foundName && foundName !== nameFromDom) {
        setNameFromDom(foundName)
      }
    }, 500) // Check every 500ms
    
    return () => {
      observer.disconnect()
      clearInterval(interval)
    }
  }, [user?.uid, nameFromDom])
  
  // Periodically check if the map has been updated for this user (handles timing issues)
  React.useEffect(() => {
    if (!user?.uid && user?.uid !== 0) return
    
    const uid = user.uid
    const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : Number(uid)
    const uidStr = String(uid)
    
    // Check if we have RTM data for this user
    const hasData = uidToUsernameMap?.[uid] || uidToUsernameMap?.[uidNum] || uidToUsernameMap?.[uidStr] ||
                   uidToUsernameMap?.[uidNum.toString()] || uidToUsernameMap?.[String(uidNum)]
    
    if (!hasData && uid !== 0) {
      // For remote users, periodically check if map has been updated
      // This handles cases where RTM data arrives after the component renders
      const interval = setInterval(() => {
        // Re-check all possible key formats
        const currentHasData = uidToUsernameMap?.[uid] || 
                              uidToUsernameMap?.[uidNum] || 
                              uidToUsernameMap?.[uidStr] ||
                              uidToUsernameMap?.[uidNum.toString()] ||
                              uidToUsernameMap?.[String(uidNum)]
        
        if (currentHasData) {
          // Force a re-render by updating state
          setRtmDataCheckCounter(prev => prev + 1)
          clearInterval(interval)
        }
      }, 500) // Check every 500ms
      
      // Clear after 15 seconds (RTM data should arrive by then)
      setTimeout(() => clearInterval(interval), 15000)
      
      return () => clearInterval(interval)
    }
  }, [user?.uid, uidToUsernameMap])
  
  // Get parsed user info from RTM username - make it reactive to uidToUsernameMap changes
  // Also use nameFromDom as a fallback to ensure we stay in sync with name pill
  const userInfo = React.useMemo(() => {
    try {
      // For local user (uid === 0 or matches local uid), check uidToUsernameMap first
      // If not found, it might be a local user that hasn't been added to map yet
      let rawUsername = null
      if (uidToUsernameMap && user?.uid !== undefined) {
        const uid = user.uid
        const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : Number(uid)
        const uidStr = String(uid)
        
        // Try multiple formats: exact match, number, string
        rawUsername = uidToUsernameMap[uid] || 
                     uidToUsernameMap[uidNum] || 
                     uidToUsernameMap[uidStr] ||
                     uidToUsernameMap[uidNum.toString()] ||
                     uidToUsernameMap[String(uidNum)]
        
        // If still not found, try to find by checking all keys (more aggressive)
        if (!rawUsername && Object.keys(uidToUsernameMap).length > 0) {
          // Check if any key matches when converted to number
          for (const key of Object.keys(uidToUsernameMap)) {
            const keyNum = parseInt(key, 10)
            if (!isNaN(keyNum) && (keyNum === uidNum || key === uidStr || key === uidNum.toString())) {
              rawUsername = uidToUsernameMap[key]
              break
            }
          }
        }
      }
      
      const hasRtmData = !!rawUsername
      const parsed = parseRtmUsername(rawUsername)
      
      // Use name from DOM (name pill) as the primary source if available, otherwise use parsed name
      // This ensures initials match what's shown in the name pill
      let actualName = null
      
      // For remote users, prioritize parsed name from RTM data (it's more reliable)
      // For local user, prioritize nameFromDom
      if (user?.uid === 0) {
        // Local user: prefer nameFromDom, then parsed, then chamberName
        if (nameFromDom && nameFromDom !== 'Loading...' && nameFromDom !== 'Unknown' && nameFromDom !== 'Guest') {
          actualName = nameFromDom
        } else if (parsed.name && parsed.name !== 'Unknown') {
          actualName = parsed.name
        } else if (chamberName) {
          actualName = chamberName
        }
      } else {
        // Remote users: ALWAYS prefer parsed name from RTM data first (most reliable)
        // Only use nameFromDom if RTM data doesn't have a name
        if (parsed.name && parsed.name !== 'Unknown' && parsed.name !== 'Guest' && parsed.name.trim().length > 0) {
          actualName = parsed.name
        } else if (nameFromDom && nameFromDom !== 'Loading...' && nameFromDom !== 'Unknown' && nameFromDom !== 'Guest') {
          actualName = nameFromDom
        } else if (hasRtmData && rawUsername) {
          // If we have RTM data but parsing failed, try to extract name from raw JSON string
          try {
            const jsonMatch = rawUsername.match(/"name"\s*:\s*"([^"]+)"/)
            if (jsonMatch && jsonMatch[1]) {
              actualName = jsonMatch[1]
            }
          } catch (e) {
            // Ignore
          }
        }
      }
      
      let displayName
      if (user?.uid === 0) {
        // For local user (chamber room), try to get name from DOM, parsed, or chamberName prop
        if (actualName) {
          displayName = actualName
        } else if (chamberName) {
          // Use chamberName prop as fallback for local user
          displayName = chamberName
        } else {
          displayName = 'Chamber Room'
        }
      } else if (actualName) {
        displayName = actualName
      } else if (!hasRtmData) {
        displayName = 'Loading...'
      } else {
        displayName = 'Guest'
      }
      
      // Update actualName if we used chamberName for local user
      if (user?.uid === 0 && !actualName && chamberName) {
        actualName = chamberName
      }
      
      // For remote users, if we still don't have actualName but have a valid displayName, use it
      // This handles cases where the name pill has been updated but nameFromDom hasn't been captured yet
      if (user?.uid !== 0 && !actualName && displayName && displayName !== 'Loading...' && displayName !== 'Guest' && displayName !== 'Unknown') {
        actualName = displayName
      }
      
      // CRITICAL: If we still don't have actualName for remote users, try to extract from name pill one more time
      // This is a last-ditch effort to get the name
      if (user?.uid !== 0 && !actualName) {
        // Try to find name pill directly in the DOM right now
        try {
          const uid = user.uid.toString()
          const uidNum = parseInt(uid)
          let videoCard = document.querySelector(`[data-uid="${uid}"]`) || 
                         document.querySelector(`[data-uid="${uidNum}"]`)
          
          if (videoCard) {
            // Look for any text element that might be the name pill
            const allTextElements = videoCard.querySelectorAll('p, span, div')
            for (const el of allTextElements) {
              const text = (el.textContent || el.innerText || '').trim()
              if (text && 
                  text.length > 0 && 
                  text.length < 100 &&
                  !text.includes('{') && 
                  !text.includes('"name"') && 
                  !text.includes('"role"') &&
                  text !== `User ${uid}` &&
                  text !== `User ${uidNum}` &&
                  text !== 'Loading...' &&
                  text !== 'Unknown' &&
                  text !== 'Guest' &&
                  !text.match(/^\d+$/) &&
                  !text.match(/^User \d+$/)) {
                actualName = text
                break
              }
            }
          }
        } catch (e) {
          console.warn('Error in last-ditch name search:', e)
        }
      }
      
      const result = {
        role: parsed.role || null,
        name: displayName,
        actualName: actualName, // Store the actual name for initials calculation (from DOM or parsed)
        profilePicId: parsed.profilePicId || null,
        hasRtmData,
      }
      
      return result
    } catch (error) {
      console.warn('Error parsing RTM username:', error)
      // Even on error, try to use name from DOM
      const fallbackName = nameFromDom || (user?.uid === 0 ? 'Me' : 'Loading...')
      return {
        role: null,
        name: fallbackName,
        actualName: nameFromDom || null,
        profilePicId: null,
        hasRtmData: false,
      }
    }
  }, [user?.uid, uidToUsernameMap, JSON.stringify(uidToUsernameMap), nameFromDom, rtmDataCheckCounter])
  
  // Get profile picture URL from profilePicId - make it reactive
  // URL format: https://media.aumhum.com/images/prod/<profilePicId>
  const profilePictureUrl = React.useMemo(() => {
    if (!userInfo.profilePicId) {
      return null
    }
    
    // Check if it's already a full URL
    if (userInfo.profilePicId.startsWith('http://') || userInfo.profilePicId.startsWith('https://')) {
      return userInfo.profilePicId
    }
    
    // Construct URL using common prefix
    // Ensure no double slashes
    const cleanProfilePicId = userInfo.profilePicId.startsWith('/') ? userInfo.profilePicId.substring(1) : userInfo.profilePicId
    const url = `${mediaUrlPrefix}${cleanProfilePicId}`
    return url
  }, [userInfo.profilePicId, userInfo.name, userInfo.actualName, user?.uid])
  
  // Extract first letter of name for avatar - use actual name from RTM data (same as name pill)
  // Make it reactive to userInfo changes
  const initials = React.useMemo(() => {
    // Use actualName if available (from parsed RTM data), otherwise fallback to display name or chamberName
    // For remote users, also try displayName if actualName is not available
    let name = userInfo.actualName
    if (!name) {
      // Try displayName as fallback (it might have been set from parsed data)
      if (userInfo.name && userInfo.name !== 'Loading...' && userInfo.name !== 'Guest' && userInfo.name !== 'Unknown') {
        name = userInfo.name
      }
    }
    if (user?.uid === 0 && !name && chamberName) {
      name = chamberName
    }
    
    // For local user
    if (user?.uid === 0) {
      // If we have actual name from RTM data or chamberName, use it
      if (name && name.length > 0) {
        const trimmedName = name.trim()
        const words = trimmedName.split(/\s+/).filter(w => w.length > 0)
        if (words.length > 1) {
          return (words[0][0] + words[words.length - 1][0]).toUpperCase()
        } else if (trimmedName.length > 0) {
          return trimmedName.charAt(0).toUpperCase()
        }
      }
      // Fallback to 'C' for "Chamber Room"
      return 'C'
    }
    
    // For remote users, use the actual parsed name from RTM data
    // This is the same name shown in the pill
    if (userInfo.actualName && userInfo.actualName.length > 0) {
      const trimmedName = userInfo.actualName.trim()
      const words = trimmedName.split(/\s+/).filter(w => w.length > 0)
      
      if (words.length > 1) {
        // Multiple words: use first letter of first and last word
        return (words[0][0] + words[words.length - 1][0]).toUpperCase()
      } else if (trimmedName.length > 0) {
        // Single word: use first letter
        return trimmedName.charAt(0).toUpperCase()
      }
    }
    
    // Also try using the 'name' variable we set earlier (which includes fallbacks)
    if (name && name.length > 0 && name !== 'Loading...' && name !== 'Guest' && name !== 'Unknown') {
      const trimmedName = name.trim()
      const words = trimmedName.split(/\s+/).filter(w => w.length > 0)
      if (words.length > 1) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase()
      } else if (trimmedName.length > 0) {
        return trimmedName.charAt(0).toUpperCase()
      }
    }
    
    // Last resort: try to extract from display name if it's not a fallback
    if (userInfo.name && userInfo.name !== 'Loading...' && userInfo.name !== 'Guest' && userInfo.name !== 'Unknown' && userInfo.name.length > 0) {
      const trimmedName = userInfo.name.trim()
      const words = trimmedName.split(/\s+/).filter(w => w.length > 0)
      if (words.length > 1) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase()
      } else if (trimmedName.length > 0) {
        return trimmedName.charAt(0).toUpperCase()
      }
    }
    
    // Only show 'U' if we truly don't have any data
    return 'U'
  }, [userInfo.actualName, userInfo.name, userInfo.hasRtmData, user?.uid, chamberName])

  // Generate a consistent modern color based on uid
  const getAvatarColor = () => {
    if (user?.uid === 0) {
      // Local user - use vibrant purple theme colors
      return {
        background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 50%, #7c3aed 100%)',
        shadow: 'rgba(139, 92, 246, 0.6)',
        glow: 'rgba(167, 139, 250, 0.4)',
      }
    }
    
    // Remote users - modern vibrant gradients
    const colors = [
      { 
        bg: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #4f46e5 100%)', 
        shadow: 'rgba(139, 92, 246, 0.6)',
        glow: 'rgba(139, 92, 246, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 50%, #e11d48 100%)', 
        shadow: 'rgba(236, 72, 153, 0.6)',
        glow: 'rgba(236, 72, 153, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%)', 
        shadow: 'rgba(6, 182, 212, 0.6)',
        glow: 'rgba(6, 182, 212, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)', 
        shadow: 'rgba(16, 185, 129, 0.6)',
        glow: 'rgba(16, 185, 129, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)', 
        shadow: 'rgba(245, 158, 11, 0.6)',
        glow: 'rgba(245, 158, 11, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #4338ca 100%)', 
        shadow: 'rgba(99, 102, 241, 0.6)',
        glow: 'rgba(99, 102, 241, 0.4)',
      },
    ]
    
    const index = (user?.uid || 0) % colors.length
    return colors[index]
  }

  const colorScheme = getAvatarColor()

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Glass morphism background
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        // Subtle gradient overlay
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(99, 102, 241, 0.08) 50%, rgba(236, 72, 153, 0.12) 100%)',
          pointerEvents: 'none',
          zIndex: 0,
        },
        // Match video card border radius to stay within bounds
        border: 'none',
        borderRadius: 'inherit', // Inherit from parent video card (16px)
        overflow: 'hidden',
        // Ensure content is above overlay
        '& > *': {
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      {/* Status indicators - positioned at bottom right */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        {/* Camera status */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            backgroundColor: '#FEE2E2',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <VideocamOffIcon sx={{ fontSize: 12, color: '#DC2626' }} />
        </Box>
        
        {/* Mic status */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            backgroundColor: hasAudio ? '#D1FAE5' : '#FEE2E2',
            border: hasAudio ? '1px solid #A7F3D0' : '1px solid #FECACA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {hasAudio ? (
            <MicIcon sx={{ fontSize: 12, color: '#059669' }} />
          ) : (
            <MicOffIcon sx={{ fontSize: 12, color: '#DC2626' }} />
          )}
        </Box>
      </Box>

      {/* Beautiful avatar with profile picture or gradient */}
      <Box
        sx={{
          position: 'relative',
          mt: 2,
        }}
      >
        <Avatar
          src={profilePictureUrl || undefined}
          sx={{
            width: 88,
            height: 88,
            background: profilePictureUrl ? 'transparent' : colorScheme.background,
            boxShadow: profilePictureUrl 
              ? `0 8px 32px ${colorScheme.shadow}, 0 0 0 4px rgba(255, 255, 255, 0.2), 0 0 0 1px ${colorScheme.glow}` 
              : `0 8px 32px ${colorScheme.shadow}, 0 4px 16px ${colorScheme.glow}, 0 0 0 4px rgba(255, 255, 255, 0.25), inset 0 2px 4px rgba(255, 255, 255, 0.3)`,
            fontSize: '36px',
            fontWeight: 700,
            border: '0',
            backdropFilter: 'blur(12px)',
            animation: 'gentle-pulse 4s ease-in-out infinite, subtle-rotate 20s linear infinite',
            objectFit: 'cover',
            color: '#ffffff',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.5)',
            letterSpacing: '1px',
            position: 'relative',
            overflow: 'visible',
            '& img': {
              objectFit: 'cover',
              width: '100%',
              height: '100%',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              right: '-4px',
              bottom: '-4px',
              background: colorScheme.background,
              borderRadius: '50%',
              opacity: 0.3,
              filter: 'blur(12px)',
              zIndex: -1,
            },
            '@keyframes gentle-pulse': {
              '0%, 100%': {
                transform: 'scale(1)',
                boxShadow: `0 8px 32px ${colorScheme.shadow}, 0 4px 16px ${colorScheme.glow}, 0 0 0 4px rgba(255, 255, 255, 0.25), inset 0 2px 4px rgba(255, 255, 255, 0.3)`,
              },
              '50%': {
                transform: 'scale(1.05)',
                boxShadow: `0 12px 40px ${colorScheme.shadow}, 0 6px 24px ${colorScheme.glow}, 0 0 0 4px rgba(255, 255, 255, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.4)`,
              },
            },
            '@keyframes subtle-rotate': {
              '0%': {
                filter: 'hue-rotate(0deg)',
              },
              '100%': {
                filter: 'hue-rotate(15deg)',
              },
            },
          }}
          imgProps={{
            onError: (e) => {
              // If image fails to load, hide src to show person icon
              e.target.style.display = 'none'
              // Clear the src to force fallback to person icon
              e.target.src = ''
              // Force re-render to show person icon
              const avatar = e.target.closest('[class*="MuiAvatar"]')
              if (avatar) {
                avatar.style.background = colorScheme.background
              }
            },
            onLoad: (e) => {
              // Ensure image is visible
              e.target.style.display = 'block'
              e.target.style.objectFit = 'cover'
            },
            crossOrigin: 'anonymous',
            loading: 'eager',
            referrerPolicy: 'no-referrer',
            style: {
              objectFit: 'cover',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
            },
          }}
        >
          {!profilePictureUrl && (
            <PersonIcon sx={{ fontSize: 52, color: 'rgba(255, 255, 255, 0.95)', filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }} />
          )}
        </Avatar>
        
        {/* Name display below the bubble */}
        {userInfo.actualName && userInfo.actualName !== 'Loading...' && userInfo.actualName !== 'Guest' && (
          <Typography
            sx={{
              mt: 1.5,
              fontSize: '14px',
              fontWeight: 600,
              color: '#1a202c',
              textAlign: 'center',
              textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {userInfo.actualName}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default ChamberVideoPlaceholder

